import * as React from "react";
import { ArrowUpRight, Rocket, ShieldCheck, TrendingUp } from "lucide-react";
import { ParallelogramButton } from "@/components/ParallelogramButton";
import { StarField } from "@/components/StarField";
import { BRAND } from "@/constants/brand";

const TRUST_ITEMS = [
  { icon: ShieldCheck, label: "Hosting & Wartung inklusive" },
  { icon: Rocket, label: "Design in 14 Tagen live" },
  { icon: TrendingUp, label: "Monatlich kündbar" },
];

/** Framing des Hero-Bildes: das Motiv liegt rechts, links bleibt Platz fuer den Text. */
const getStageFraming = (width) => {
  if (width >= 1280) return { shiftX: 0, scale: 1, position: "center right" };
  if (width >= 768) return { shiftX: 0, scale: 1.05, position: "center right" };
  return { shiftX: 0, scale: 1.25, position: "72% center" };
};

export const HeroSection = () => {
  const [framing, setFraming] = React.useState({
    shiftX: 0,
    scale: 1,
    position: "center right",
  });
  const [parallaxY, setParallaxY] = React.useState(0);

  // Responsives Framing
  React.useEffect(() => {
    const onResize = () => setFraming(getStageFraming(window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Scroll-Parallax: Planet wandert langsamer als der Inhalt
  React.useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setParallaxY(Math.min(window.scrollY, 900) * 0.22);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      id="top"
      className="grain relative isolate flex min-h-[100svh] w-full items-center overflow-hidden bg-background"
    >
      {/* Pure black base so the scaled hero image blends seamlessly */}
      <div aria-hidden className="absolute inset-0 -z-40 bg-[hsl(0_0%_0%)]" />

      {/* Media stage: Hero-Bild, nach rechts versetzt, mit Scroll-Parallax */}
      <div
        aria-hidden
        className="absolute inset-0 -z-20 origin-center transform-gpu will-change-transform"
        style={{
          transform: `translate3d(${framing.shiftX}%, ${parallaxY}px, 0) scale(${framing.scale})`,
        }}
      >
        {/* Hero-Bild */}
        <div
          data-testid="hero-background-image"
          className="absolute inset-0 bg-cover brightness-[1.05] contrast-[1.02] saturate-[1.05]"
          style={{
            backgroundImage: `url(${BRAND.heroImage})`,
            backgroundPosition: framing.position,
          }}
        />

        {/* Weiche Kanten, damit das Framing nahtlos in Schwarz auslaeuft */}
        <div className="absolute inset-0 shadow-[inset_0_0_140px_60px_hsl(0_0%_0%)]" />
      </div>

      {/* Legibility veils */}
      <div className="veil-left absolute inset-0 -z-10" aria-hidden />
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(120%_95%_at_55%_-5%,transparent_45%,hsl(240_30%_2%/0.35)_80%,hsl(240_30%_2%/0.85)_100%)]"
        aria-hidden
      />
      <div
        className="absolute inset-x-0 bottom-0 -z-10 h-52 bg-[linear-gradient(180deg,transparent,hsl(240_24%_3%/0.7)_60%,hsl(240_24%_3%)_100%)]"
        aria-hidden
      />
      <StarField count={70} className="-z-10 opacity-70" />

      {/* Content */}
      <div
        className="relative z-10 mx-auto w-full max-w-[1500px] px-5 pb-24 pt-32 sm:px-8 sm:pb-28 sm:pt-36 lg:px-12 lg:pb-32 lg:pt-40"
        style={{
          transform: `translate3d(0, ${-parallaxY * 0.35}px, 0)`,
          opacity: Math.max(0, 1 - parallaxY / 260),
        }}
      >
        <div className="max-w-[46rem]">
          <div className="inline-flex flex-col animate-fade-in">
            <span className="h-px w-full bg-gradient-hairline" aria-hidden />
            <span className="flex items-center gap-3 py-3.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-glow opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary-glow" />
              </span>
              <span className="font-display text-[11px] font-light uppercase tracking-[0.3em] text-foreground/85">
                Webdesign aus einer Hand
              </span>
            </span>
            <span className="h-px w-full bg-gradient-hairline" aria-hidden />
          </div>

          <h1 className="mt-8 font-display text-4xl font-light leading-[1.06] tracking-tight text-foreground sm:text-5xl lg:text-7xl">
            <span className="block animate-fade-up" style={{ animationDelay: "0.05s" }}>
              Der beste Zug
            </span>
            <span className="block animate-fade-up" style={{ animationDelay: "0.18s" }}>
              für deine Webseite.
            </span>
          </h1>

          <div
            className="mt-9 h-px w-44 bg-gradient-hairline animate-fade-in"
            style={{ animationDelay: "0.3s" }}
            aria-hidden
          />

          <p
            className="mt-8 max-w-xl text-sm font-light leading-relaxed text-muted-foreground animate-fade-up sm:text-base lg:text-lg"
            style={{ animationDelay: "0.32s" }}
          >
            Maßgeschneiderte Websites inklusive Hosting, Wartung und
            persönlichem Support – für 59 € im Monat.
          </p>

          <div
            className="mt-11 flex flex-col gap-4 animate-fade-up sm:flex-row sm:items-center"
            style={{ animationDelay: "0.45s" }}
          >
            <ParallelogramButton
              variant="primary"
              size="lg"
              data-testid="hero-primary-cta"
              className="font-display font-normal"
            >
              Kostenlosen Entwurf sichern
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </ParallelogramButton>

            <ParallelogramButton
              variant="outline"
              size="lg"
              data-testid="hero-secondary-cta"
              className="font-display font-normal"
            >
              Referenzen ansehen
            </ParallelogramButton>
          </div>

          <div
            className="mt-14 flex flex-wrap items-end gap-x-4 gap-y-2 animate-fade-up"
            style={{ animationDelay: "0.58s" }}
          >
            <span className="font-display text-5xl font-extralight leading-none tracking-tight text-foreground sm:text-6xl lg:text-7xl">
              59&nbsp;€
            </span>
            <span className="pb-1.5 font-display text-lg font-light tracking-wide text-primary-glow sm:text-xl">
              / Monat
            </span>
            <span className="pb-2 pl-1 text-xs font-light text-muted-foreground sm:text-sm">
              keine Einrichtungsgebühr
            </span>
          </div>

          <ul
            className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-4 animate-fade-in"
            style={{ animationDelay: "0.75s" }}
          >
            {TRUST_ITEMS.map(({ icon: Icon, label }, index) => (
              <li key={label} className="flex items-center gap-2.5">
                {index > 0 && (
                  <span
                    aria-hidden
                    className="mr-5 hidden h-4 w-px bg-border sm:block"
                  />
                )}
                <Icon className="h-4 w-4 shrink-0 text-primary-glow" strokeWidth={1.5} />
                <span className="text-xs font-light tracking-wide text-muted-foreground sm:text-sm">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

    </section>
  );
};
